/**
 * @file TaskRow.tsx
 * @description Строка страницы «Задачи» (phase19.31, SC60): флажок слева,
 * название (и подробности), вид, к кому относится, исполнитель, срок.
 *
 * От 1024 — колонки как в макете; уже — две строки: «☐ Название» /
 * «Звонок · Мария Ч. · Анна К. · ▲ 19.09, 18:11». Нажатие на строку
 * открывает окно задачи (кнопка растянута на всю строку), флажок и ссылки
 * клиента/сделки/заказа лежат поверх неё. Флажок — только у того, кто может
 * отметить выполненной (T4); у выполненной — отмеченный, без действия.
 */

import { TriangleAlert } from "lucide-react";
import { Link } from "react-router";

import { Checkbox } from "../../ui/checkbox";
import { ACTIVITY_KIND_LABEL, type ActivityDto } from "../../../lib/api-crm";
import type { TaskGroupKey } from "./task-groups";
import { taskAssigneeText, taskDueText, taskLink } from "./task-texts";
import { cn } from "@/lib/utils";

interface Props {
  task: ActivityDto;
  group: TaskGroupKey;
  timeZone: string;
  /** База адресов кабинета магазина: `/shop/<slug>`. */
  base: string;
  canComplete: boolean;
  completing: boolean;
  onComplete: () => void;
  onOpen: () => void;
}

/** Срок: просрочка — янтарный значок у срока, а не слово в названии. */
function TaskDue({ text, overdue }: { text: string; overdue: boolean }) {
  if (!text) return null;
  return (
    <span className={cn("inline-flex items-center gap-1 tabular-nums", overdue && "font-medium text-[var(--status-attention-fg)]")}>
      {overdue ? <TriangleAlert aria-hidden className="size-3.5 shrink-0" /> : null}
      {overdue ? <span className="sr-only">Просрочено, срок </span> : null}
      {text}
    </span>
  );
}

/** Клиент и сделка/заказ — ссылками (лежат поверх растянутой кнопки строки). */
function TaskRelation({ task, base }: { task: ActivityDto; base: string }) {
  const link = taskLink(task);
  const to = link ? (link.kind === "deal" ? `${base}/crm/deals/${link.id}` : `${base}/orders/${link.id}`) : null;
  const linkClass = "relative z-10 rounded-sm underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-ring";
  return (
    <span className="min-w-0 break-words">
      {task.customerName ? (
        <Link to={`${base}/customers/${task.customerId}`} className={linkClass}>
          {task.customerName}
        </Link>
      ) : null}
      {task.customerName && link ? " · " : null}
      {link && to ? (
        <Link to={to} className={linkClass}>
          {link.text}
        </Link>
      ) : null}
    </span>
  );
}

export function TaskRow({ task, group, timeZone, base, canComplete, completing, onComplete, onOpen }: Props) {
  const done = !!task.completedAt;
  const due = taskDueText(task, group, timeZone);
  const overdue = group === "overdue";
  const phoneMeta = [ACTIVITY_KIND_LABEL[task.kind], task.customerName, taskAssigneeText(task)].filter(Boolean).join(" · ");

  return (
    <li
      className={cn(
        "relative flex items-start gap-1 pr-3 transition-colors hover:bg-surface-low",
        "lg:grid lg:grid-cols-[3rem_minmax(0,2fr)_6rem_minmax(0,2fr)_10rem_8.5rem] lg:items-center lg:gap-3",
      )}
    >
      <span className="relative z-10 flex size-12 shrink-0 items-center justify-center">
        {canComplete || done ? (
          <label className="flex size-12 cursor-pointer items-center justify-center">
            <Checkbox
              aria-label={done ? `Выполнена: ${task.title}` : `Отметить выполненной: ${task.title}`}
              checked={done || completing}
              disabled={done || completing}
              onCheckedChange={(checked) => checked === true && onComplete()}
            />
          </label>
        ) : null}
      </span>

      <button
        type="button"
        onClick={onOpen}
        className={cn(
          "flex min-h-12 min-w-0 flex-1 flex-col justify-center gap-0.5 py-2 text-left outline-none",
          "after:absolute after:inset-0 after:content-[''] focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-ring",
        )}
      >
        <span className={cn("type-body break-words font-medium", done ? "text-on-surface-variant line-through" : "text-on-surface")}>
          {task.title}
        </span>
        {task.content ? <span className="type-caption hidden break-words text-on-surface-variant lg:line-clamp-2">{task.content}</span> : null}
        <span className="type-caption flex flex-wrap items-center gap-x-1 text-on-surface-variant lg:hidden">
          <span>{phoneMeta}</span>
          {due ? <span aria-hidden>·</span> : null}
          <TaskDue text={due} overdue={overdue} />
        </span>
      </button>

      <span className="type-body hidden text-on-surface-variant lg:block">{ACTIVITY_KIND_LABEL[task.kind]}</span>
      <span className="type-body hidden text-on-surface lg:block">
        <TaskRelation task={task} base={base} />
      </span>
      <span className={cn("type-body hidden truncate lg:block", task.assigneeName ? "text-on-surface" : "text-on-surface-variant")}>
        {taskAssigneeText(task)}
      </span>
      <span className="type-body hidden text-right lg:block">
        <TaskDue text={due} overdue={overdue} />
      </span>
    </li>
  );
}
